import React, { useEffect, useRef, useState } from 'react';
import { FaSync, FaChartLine } from 'react-icons/fa';
import api from '../../../api';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const SCOPES = [
  { key: 'financialYear', label: 'Financial Year' },
  { key: 'currentYear', label: 'Current Year' },
  { key: 'both', label: 'Both' },
];

const inr = (n) =>
  n === null || n === undefined || n === '' || Number.isNaN(Number(n))
    ? '-'
    : `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const num = (n) =>
  n === null || n === undefined || n === '' || Number.isNaN(Number(n))
    ? '-'
    : Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const pct = (n) =>
  n === null || n === undefined || n === ''
    ? '-'
    : `${n > 0 ? '+' : ''}${Number(n).toFixed(2)}%`;

const dateOnly = (v) => (v ? new Date(v).toLocaleDateString('en-IN') : '-');

function currentFinancialYear(now = new Date()) {
  const y = now.getFullYear();
  const start = now.getMonth() + 1 >= 4 ? y : y - 1;
  return `${start}-${start + 1}`;
}

function fyOptions(now = new Date()) {
  const startYear = Number(currentFinancialYear(now).slice(0, 4));
  return Array.from({ length: 7 }, (_, i) => {
    const s = startYear - 3 + i;
    return `${s}-${s + 1}`;
  });
}

function formatValue(value, format) {
  if (format === 'percent') {
    return value === null || value === undefined ? '-' : `${Number(value).toFixed(2)}%`;
  }
  if (format === 'number') return num(value);
  return inr(value);
}

const TallyReports = () => {
  const [financialYear, setFinancialYear] = useState(currentFinancialYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [scope, setScope] = useState('financialYear');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Date range (used only when a report is configured with periodMode 'dateRange').
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Debtor report controls
  const [debtorSearch, setDebtorSearch] = useState('');
  const [debtorPage, setDebtorPage] = useState(1);
  const [debtorOnlyUnpaid, setDebtorOnlyUnpaid] = useState(false);
  const DEBTOR_PAGE_SIZE = 25;

  // Monotonic request id so a slower earlier response can't overwrite a newer one.
  const fetchSeq = useRef(0);

  const fetchReport = async () => {
    const seq = ++fetchSeq.current;
    setLoading(true);
    setError('');
    try {
      const params = { financialYear, month, scope };
      if (dateFrom && dateTo) {
        params.dateFrom = dateFrom;
        params.dateTo = dateTo;
      }
      const res = await api.get('/reports/tally/summary', { params });
      if (seq !== fetchSeq.current) return; // stale response — a newer request is in flight
      setData(res.data);
    } catch (err) {
      if (seq !== fetchSeq.current) return;
      setError(err.response?.data?.message || 'Failed to load tally reports.');
      setData(null);
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financialYear, month, scope, dateFrom, dateTo]);

  // True when any configured report uses an explicit From/To date range.
  const needsDateRange = (data?.reports || []).some((r) => r.periodMode === 'dateRange');

  // Default the From/To range to the selected month the first time we learn a
  // date-range report is present.
  useEffect(() => {
    if (!needsDateRange || (dateFrom && dateTo)) return;
    const startYear = Number(String(financialYear).slice(0, 4));
    const year = month >= 4 ? startYear : startYear + 1;
    const fmt = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setDateFrom(fmt(new Date(year, month - 1, 1)));
    setDateTo(fmt(new Date(year, month, 0)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsDateRange, financialYear, month]);

  const renderGrowth = (value) => {
    const negative = value !== null && value !== undefined && value < 0;
    return (
      <span className={negative ? 'text-red-600 dark:text-red-400' : 'text-green-700 dark:text-green-400'}>
        {pct(value)}
      </span>
    );
  };

  const comparisonGroups = data?.comparisonGroups || [];
  const periodLabels = data?.periodLabels || {};
  const ongoing = periodLabels.ongoing || {};
  const withOngoing = (label, on) => `${label}${on ? ' (Ongoing)' : ''}`;
  const labels = {
    previous: withOngoing(periodLabels.previous || 'Previous Month', ongoing.previous),
    current: withOngoing(periodLabels.current || 'Selected Month', ongoing.current),
    next: withOngoing(periodLabels.next || 'Next Month', ongoing.next),
    lastYearSameMonth: periodLabels.lastYearSameMonth || 'Last Year Same Month',
  };

  const monthCell = (value, format, growth) => (
    <td className="px-3 py-2 text-right align-top">
      <div>{formatValue(value, format)}</div>
      {growth !== null && growth !== undefined && (
        <div className="text-[11px]">{renderGrowth(growth)}</div>
      )}
    </td>
  );

  const card = 'rounded border overflow-x-auto bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700';
  const thead = 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
  const rowBorder = 'border-gray-200 dark:border-gray-700';
  const rowAlt = 'even:bg-gray-50 dark:even:bg-gray-700/30';
  const cellStrong = 'text-gray-800 dark:text-gray-100';
  const inputCls =
    'w-full border rounded p-2 text-sm bg-white border-gray-300 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100';
  const labelCls = 'block text-xs font-medium mb-1 text-gray-600 dark:text-gray-400';

  const renderMeasuresTable = (report) => (
    <div className={`${card} mb-4`}>
      <table className="min-w-full text-sm">
        <thead className={thead}>
          <tr>
            <th className="px-3 py-2 text-left font-semibold">KPI</th>
            <th className="px-3 py-2 text-right font-semibold">{labels.previous}</th>
            <th className="px-3 py-2 text-right font-semibold">{labels.current}</th>
            <th className="px-3 py-2 text-right font-semibold">{labels.next}</th>
            <th className="px-3 py-2 text-right font-semibold">{labels.lastYearSameMonth}</th>
            <th className="px-3 py-2 text-right font-semibold">LYSM Growth %</th>
            {comparisonGroups.map((g) => (
              <React.Fragment key={g.key}>
                <th className="px-3 py-2 text-right font-semibold">{g.label} YTD</th>
                <th className="px-3 py-2 text-right font-semibold">{g.label} LYTD</th>
                <th className="px-3 py-2 text-right font-semibold">{g.label} Growth %</th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {report.measures.filter((m) => !m.hidden).map((m) => (
            <tr key={m.key} className={`border-t ${rowBorder} ${rowAlt}`}>
              <td className={`px-3 py-2 font-medium ${cellStrong}`}>{m.label}</td>
              {monthCell(m.previous, m.format, null)}
              {monthCell(m.current, m.format, m.monthlyGrowth)}
              {monthCell(m.next, m.format, m.nextGrowth)}
              <td className="px-3 py-2 text-right">{formatValue(m.lastYearSameMonth, m.format)}</td>
              <td className="px-3 py-2 text-right">{renderGrowth(m.lysmGrowth)}</td>
              {comparisonGroups.map((g) => {
                const c = m.comparisons ? m.comparisons[g.key] : null;
                return (
                  <React.Fragment key={g.key}>
                    <td className="px-3 py-2 text-right">{c ? formatValue(c.ytd, m.format) : '-'}</td>
                    <td className="px-3 py-2 text-right">{c ? formatValue(c.lytd, m.format) : '-'}</td>
                    <td className="px-3 py-2 text-right">{c ? renderGrowth(c.growth) : '-'}</td>
                  </React.Fragment>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderBreakdown = (breakdown) => {
    const dims = breakdown.dimensions || [];
    return (
      <div className={`${card} mb-4`}>
        {breakdown.measureLabel && (
          <div className={`px-3 py-2 text-xs font-semibold uppercase border-b ${rowBorder} text-gray-600 dark:text-gray-400`}>
            {breakdown.measureLabel}
          </div>
        )}
        <table className="min-w-full text-sm">
          <thead className={thead}>
            <tr>
              {dims.map((d) => (
                <th key={d.key || d.label} className="px-3 py-2 text-left font-semibold">
                  {d.label}
                </th>
              ))}
              <th className="px-3 py-2 text-right font-semibold">{labels.previous}</th>
              <th className="px-3 py-2 text-right font-semibold">{labels.current}</th>
              <th className="px-3 py-2 text-right font-semibold">{labels.next}</th>
              <th className="px-3 py-2 text-right font-semibold">{labels.lastYearSameMonth}</th>
              <th className="px-3 py-2 text-right font-semibold">LYSM Growth %</th>
            </tr>
          </thead>
          <tbody>
            {breakdown.rows.length === 0 ? (
              <tr className={`border-t ${rowBorder}`}>
                <td
                  colSpan={dims.length + 5}
                  className="px-3 py-4 text-center text-gray-500 dark:text-gray-400"
                >
                  No data for the selected period.
                </td>
              </tr>
            ) : (
              breakdown.rows.map((row, idx) => (
                <tr key={idx} className={`border-t ${rowBorder} ${rowAlt}`}>
                  {(row.values || []).map((val, i) => (
                    <td key={i} className={`px-3 py-2 font-medium ${cellStrong}`}>{val}</td>
                  ))}
                  {monthCell(row.lastMonth, breakdown.format, null)}
                  {monthCell(row.current, breakdown.format, row.monthlyGrowth)}
                  {monthCell(row.nextMonth, breakdown.format, row.nextGrowth)}
                  <td className="px-3 py-2 text-right">
                    {formatValue(row.lastYearSameMonth, breakdown.format)}
                  </td>
                  <td className="px-3 py-2 text-right">{renderGrowth(row.lysmGrowth)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderMatrix = (breakdown, report) => {
    const dims = breakdown.dimensions || [];
    const measures = breakdown.measures || [];
    const hasPercent = !!breakdown.percentKey;
    const exportMatrix = () => {
      const header = [...dims.map((d) => d.label), ...measures.map((m) => m.label), ...(hasPercent ? ['% Share'] : [])];
      const body = breakdown.rows.map((r) => [
        ...dims.map((_, i) => r.values[i]),
        ...measures.map((m) => (r.measures ? r.measures[m.key] : 0)),
        ...(hasPercent ? [r.percent ?? 0] : []),
      ]);
      const totalRow = [
        'TOTAL',
        ...dims.slice(1).map(() => ''),
        ...measures.map((m) => breakdown.totals?.[m.key] ?? 0),
        ...(hasPercent ? [100] : []),
      ];
      const csv = [header, ...body, totalRow]
        .map((r) => r.map((v) => `"${String(v === null || v === undefined ? '' : v).replace(/"/g, '""')}"`).join(','))
        .join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(report.label || 'report').replace(/\s+/g, '_')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    };

    return (
      <div className={`${card} mb-4`}>
        <div className="px-3 py-2 text-xs font-semibold uppercase border-b text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700">
          {report.label}
        </div>
        <table className="min-w-full text-sm">
          <thead className={thead}>
            <tr>
              <th className="px-3 py-2 text-left font-semibold">#</th>
              {dims.map((d) => (
                <th key={d.key || d.label} className="px-3 py-2 text-left font-semibold">
                  {d.label}
                </th>
              ))}
              {measures.map((m) => (
                <th key={m.key} className="px-3 py-2 text-right font-semibold">
                  {m.label}
                </th>
              ))}
              {hasPercent && <th className="px-3 py-2 text-right font-semibold">% Share</th>}
            </tr>
          </thead>
          <tbody>
            {breakdown.rows.length === 0 ? (
              <tr className={`border-t ${rowBorder}`}>
                <td colSpan={dims.length + measures.length + 1 + (hasPercent ? 1 : 0)} className="px-3 py-4 text-center text-gray-500 dark:text-gray-400">
                  No data for the selected period.
                </td>
              </tr>
            ) : (
              breakdown.rows.map((row, idx) => (
                <tr key={idx} className={`border-t ${rowBorder} ${rowAlt}`}>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{idx + 1}</td>
                  {dims.map((_, i) => (
                    <td key={i} className={`px-3 py-2 font-medium ${cellStrong}`}>{row.values[i]}</td>
                  ))}
                  {measures.map((m) => (
                    <td key={m.key} className="px-3 py-2 text-right">
                      {formatValue(row.measures ? row.measures[m.key] : 0, m.format)}
                    </td>
                  ))}
                  {hasPercent && (
                    <td className="px-3 py-2 text-right">
                      {row.percent === null || row.percent === undefined ? '-' : `${Number(row.percent).toFixed(2)}%`}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
          <tfoot>
            <tr className={`border-t-2 font-semibold ${rowBorder} bg-gray-100 dark:bg-gray-700/40`}>
              <td className="px-3 py-2" />
              <td className="px-3 py-2" colSpan={dims.length}>
                TOTAL OF SALES
              </td>
              {measures.map((m) => (
                <td key={m.key} className="px-3 py-2 text-right">
                  {formatValue(breakdown.totals ? breakdown.totals[m.key] : 0, m.format)}
                </td>
              ))}
              {hasPercent && <td className="px-3 py-2 text-right">100.00%</td>}
            </tr>
          </tfoot>
        </table>
        <div className="p-2">
          <button
            type="button"
            onClick={exportMatrix}
            className="px-3 py-1.5 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-700"
          >
            Export CSV
          </button>
        </div>
      </div>
    );
  };

  const exportDebtorCsv = (report, rows) => {
    const bucketLabels = (report.buckets || []).map((b) => b.label);
    const header = [
      'Debtor',
      'Group',
      'Opening',
      'Ledger Balance',
      'FY Outstanding',
      'Unpaid Invoices',
      ...bucketLabels,
    ];
    const body = rows.map((d) => [
      d.name,
      d.group,
      d.opening,
      d.closingBalance,
      d.pending,
      d.bills ? d.bills.length : 0,
      ...bucketLabels.map((l) => (d.bucketTotals ? d.bucketTotals[l] || 0 : 0)),
    ]);
    const csv = [header, ...body]
      .map((r) => r.map((v) => `"${String(v === null || v === undefined ? '' : v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(report.label || 'debtors').replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderDebtorReport = (report) => {
    // Prioritise debtors that have unpaid invoices, then by outstanding size.
    const sorted = [...report.debtors].sort((a, b) => {
      const ap = (a.pending || 0) > 0 ? 1 : 0;
      const bp = (b.pending || 0) > 0 ? 1 : 0;
      if (ap !== bp) return bp - ap;
      if ((b.pending || 0) !== (a.pending || 0)) return (b.pending || 0) - (a.pending || 0);
      return Math.abs(b.closingBalance || 0) - Math.abs(a.closingBalance || 0);
    });

    const q = debtorSearch.trim().toLowerCase();
    let filtered = sorted;
    if (debtorOnlyUnpaid) filtered = filtered.filter((d) => (d.pending || 0) > 0);
    if (q) {
      filtered = filtered.filter(
        (d) => d.name.toLowerCase().includes(q) || (d.group || '').toLowerCase().includes(q),
      );
    }

    const totalPages = Math.max(1, Math.ceil(filtered.length / DEBTOR_PAGE_SIZE));
    const page = Math.min(debtorPage, totalPages);
    const pageRows = filtered.slice((page - 1) * DEBTOR_PAGE_SIZE, page * DEBTOR_PAGE_SIZE);
    const unpaidCount = sorted.filter((d) => (d.pending || 0) > 0).length;

    return (
      <>
        <div className="mb-2 text-sm text-gray-600 dark:text-gray-400">
          Period {dateOnly(report.period?.from)} – {dateOnly(report.period?.to)} · FY Outstanding{' '}
          <strong>{inr(report.totals?.pending)}</strong> · Opening{' '}
          <strong>{inr(report.totals?.opening)}</strong> · Ledger balance (receivable +){' '}
          <strong>{inr(report.totals?.closingBalance)}</strong>
        </div>

        {/* controls */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <input
            type="text"
            value={debtorSearch}
            onChange={(e) => {
              setDebtorSearch(e.target.value);
              setDebtorPage(1);
            }}
            placeholder="Search debtor / group"
            className={`w-64 ${inputCls}`}
          />
          <label className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
            <input
              type="checkbox"
              checked={debtorOnlyUnpaid}
              onChange={(e) => {
                setDebtorOnlyUnpaid(e.target.checked);
                setDebtorPage(1);
              }}
            />
            Only unpaid invoices ({unpaidCount})
          </label>
          <button
            type="button"
            onClick={() => exportDebtorCsv(report, filtered)}
            className="px-3 py-2 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-700"
          >
            Export CSV
          </button>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {filtered.length} debtor(s)
          </span>
        </div>

        {pageRows.length === 0 ? (
          <div className="p-6 text-center text-gray-500 dark:text-gray-400">
            No outstanding debtors for the selected period.
          </div>
        ) : (
          pageRows.map((d) => (
            <div key={d.name} className={`${card} mb-2 p-3`}>
              <div className="flex flex-wrap justify-between gap-2">
                <div className={`font-semibold ${cellStrong}`}>
                  {d.name}{' '}
                  <span className="text-xs font-normal text-gray-500 dark:text-gray-400">({d.group})</span>
                </div>
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Opening: <strong>{inr(d.opening)}</strong> · Ledger balance:{' '}
                  <strong>{inr(d.closingBalance)}</strong> · FY Outstanding:{' '}
                  <strong>{inr(d.pending)}</strong>
                </div>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {(report.buckets || [])
                  .map((b) => `${b.label}: ${inr(d.bucketTotals ? d.bucketTotals[b.label] || 0 : 0)}`)
                  .join(' · ')}
              </div>
              <details className="mt-2" open={(d.pending || 0) > 0 && pageRows.length <= 10}>
                <summary className="cursor-pointer text-sm text-indigo-600 dark:text-indigo-400">
                  Unpaid invoices ({d.bills.length})
                </summary>
                <div className="overflow-x-auto mt-2 border rounded dark:border-gray-700">
                  <table className="min-w-full text-sm">
                    <thead className={thead}>
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">Invoice</th>
                        <th className="px-3 py-2 text-left font-semibold">Type</th>
                        <th className="px-3 py-2 text-left font-semibold">Date</th>
                        <th className="px-3 py-2 text-left font-semibold">Due Date</th>
                        <th className="px-3 py-2 text-right font-semibold">Amount</th>
                        <th className="px-3 py-2 text-right font-semibold">Settled</th>
                        <th className="px-3 py-2 text-right font-semibold">Pending</th>
                        <th className="px-3 py-2 text-right font-semibold">Age (days)</th>
                        <th className="px-3 py-2 text-left font-semibold">Bucket</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.bills.map((b, i) => (
                        <tr key={i} className={`border-t ${rowBorder}`}>
                          <td className="px-3 py-2 font-medium">{b.voucherNumber}</td>
                          <td className="px-3 py-2">{b.voucherTypeName}</td>
                          <td className="px-3 py-2">{dateOnly(b.date)}</td>
                          <td className="px-3 py-2">{b.dueDate ? dateOnly(b.dueDate) : '-'}</td>
                          <td className="px-3 py-2 text-right">{inr(b.amount)}</td>
                          <td className="px-3 py-2 text-right">{inr(b.settled)}</td>
                          <td className="px-3 py-2 text-right font-medium">{inr(b.pending)}</td>
                          <td className="px-3 py-2 text-right">{b.ageDays}</td>
                          <td className="px-3 py-2">{b.bucket}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </div>
          ))
        )}

        {/* pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-3">
            <button
              type="button"
              onClick={() => setDebtorPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded text-sm disabled:opacity-50"
            >
              Prev
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setDebtorPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 border rounded text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="p-4 md:p-6 min-h-full bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
      <div className="flex items-center gap-2 mb-4">
        <FaChartLine className="text-indigo-600 dark:text-indigo-400" />
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Tally Reports</h2>
      </div>

      <div className={`grid grid-cols-1 sm:grid-cols-2 ${needsDateRange ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-3 mb-4 p-3 rounded border bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700`}>
        <div>
          <label className={labelCls}>Financial Year</label>
          <select value={financialYear} onChange={(e) => setFinancialYear(e.target.value)} className={inputCls}>
            {fyOptions().map((fy) => (
              <option key={fy} value={fy}>{fy}</option>
            ))}
          </select>
        </div>
        {needsDateRange ? (
          <>
            <div>
              <label className={labelCls}>From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className={inputCls}
              />
            </div>
          </>
        ) : (
          <div>
            <label className={labelCls}>Month</label>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={inputCls}>
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className={labelCls}>Comparison</label>
          <select value={scope} onChange={(e) => setScope(e.target.value)} className={inputCls}>
            {SCOPES.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <button
            onClick={fetchReport}
            disabled={loading}
            className="px-3 py-2 bg-indigo-600 text-white rounded text-sm flex items-center gap-2 hover:bg-indigo-700 disabled:opacity-60"
          >
            <FaSync className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 rounded text-sm border bg-red-50 border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="p-10 text-center text-gray-500 dark:text-gray-400">Loading reports...</div>
      )}

      {data &&
        data.reports.map((report) => (
          <div key={report.key} className="mb-8">
            <h3 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-100">{report.label}</h3>

            {report.type === 'debtor' ? (
              renderDebtorReport(report)
            ) : (
              <>
                {report.alerts?.debtorDays && report.alerts.debtorDays.limit > 0 && (
                  <div className="flex flex-wrap gap-3 mb-2">
                    <div
                      className={`px-3 py-2 rounded border text-sm font-medium ${
                        report.alerts.debtorDays.status === 'WATCH'
                          ? 'bg-amber-50 border-amber-300 text-amber-800 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300'
                          : 'bg-green-50 border-green-300 text-green-800 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                      }`}
                    >
                      Debtor Days: {report.alerts.debtorDays.status} - Debtor Days{' '}
                      {report.alerts.debtorDays.value} (Limit {report.alerts.debtorDays.limit})
                    </div>
                  </div>
                )}

                {report.breakdownMode === 'matrix' && report.breakdown ? (
                  renderMatrix(report.breakdown, report)
                ) : (
                  <>
                    {renderMeasuresTable(report)}
                    {report.breakdown && renderBreakdown(report.breakdown)}
                  </>
                )}
              </>
            )}
          </div>
        ))}
    </div>
  );
};

export default TallyReports;
